import React, { useState, useEffect } from "react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

interface LocationSearchInputProps {
  value: string;
  onChange: (value: string, location?: { lat: number; lng: number }) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  onLocationSelect?: (location: { lat: number; lng: number }) => void;
}

export default function LocationSearchInput({
  value,
  onChange,
  placeholder = "Buscar dirección...",
  className = "",
  error,
  disabled = false,
  onLocationSelect,
}: LocationSearchInputProps) {
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete();

  const [showSuggestions, setShowSuggestions] = useState(false);

  // Handle location selection
  const handleSelect = async (address: string) => {
    setValue(address, false);
    clearSuggestions();
    setShowSuggestions(false);
    onChange(address);

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      const location = { lat, lng };
      onLocationSelect?.(location);
      onChange(address, location);
    } catch (error) {
      console.error("Error getting location:", error);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    onChange(inputValue);
    setShowSuggestions(true);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (ready && value) {
      setShowSuggestions(true);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowSuggestions(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Filter suggestions based on input
  const suggestions =
    status === "OK"
      ? data.slice(0, 5).map(({ description, place_id }) => ({
          value: description,
          label: description,
          place_id,
        }))
      : [];

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled || !ready}
          className={`w-full px-3 py-2.5 pr-10 border border-transparent rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all ${
            error ? "border-red-500/60" : ""
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
        />

        {/* Search Icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {ready ? (
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          ) : (
            <div className="w-5 h-5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-card border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSelect(suggestion.value)}
              className="w-full px-4 py-3 text-left text-sm hover:bg-gray-100 dark:hover:bg-dark-card transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0 flex items-start gap-3"
            >
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span className="text-gray-900 dark:text-white line-clamp-2">
                {suggestion.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
